import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  deleteAppointment,
  updateAppointment,
} from 'src/models/appointment/appointment';
import { isAppointmentFuture } from 'src/models/appointment/utils';
import { VisitStatus } from 'src/models/visit/_types';
import toast from 'src/utils/toast';

import type { Appointment } from 'src/models/appointment/_types';
import type { User } from 'src/models/user/_types';

export const useAppointmentDrawer = ({ appointment, onClose }) => {
  const queryClient = useQueryClient();
  const [openDialog, setOpenDialog] = useState('');
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addUserTab, setAddUserTab] = useState<'attendees' | 'waitlist'>(
    'attendees'
  );
  const [visitId, setVisitId] = useState<number | null>(null);
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [messageAttendees, setMessageAttendees] = useState<User[]>([]);

  const visit = appointment && appointment.visits.find((v) => v.id === visitId);

  const updateAppointmentMutation = useMutation({
    mutationFn: (appointment: Appointment) => {
      return updateAppointment(appointment);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      toast.success('Event successfully canceled');
      setOpenDialog('');
      onClose();
    },
    onError: () => {
      toast.error('Event could not be canceled');
    },
  });
  const deleteAppointmentMutation = useMutation({
    mutationFn: (appointment: Partial<Appointment>) => {
      return deleteAppointment(appointment);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      toast.success('Appointment successfully deleted');
      setOpenDialog('');
      onClose();
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      toast.error('Appointment could not be deleted');
    },
  });

  const handleAppointmentCancel = () => {
    updateAppointmentMutation.mutate({ ...appointment, status: 'cancelled' });
  };

  const handleAppointmentDelete = () => {
    deleteAppointmentMutation.mutate(appointment);
  };

  const attendeeVisits = appointment?.visits.filter(
    (v) =>
      v.status === VisitStatus.Completed ||
      v.status === VisitStatus.Reserved ||
      v.status === VisitStatus.Registered
  );

  return {
    visit,
    setVisitId,
    showAppointmentDialog,
    setShowAppointmentDialog,
    handleAppointmentCancel,
    handleAppointmentDelete,
    openDialog,
    setOpenDialog,
    isAppointmentFuture,
    addUserOpen,
    setAddUserOpen,
    addUserTab,
    setAddUserTab,
    messageAttendees,
    setMessageAttendees,
    attendeeVisits,
  };
};
